import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

const toolName = 'delete-unused-styles' as const;

export const deleteUnusedStylesArgsSchema = z.object({
  styleBlockIds: z
    .array(z.string())
    .min(1)
    .describe(
      dedent`
        The styleBlockId values returned by find-unused-styles for the styles the user has confirmed they want to delete.
        Pass the exact ids — do not pass class names or selectors. Pass at least one id.
      `
    ),
});

export type DeleteUnusedStylesArgs = z.infer<
  typeof deleteUnusedStylesArgsSchema
>;

export const deleteUnusedStylesTool = defineToolRegistryManifest({
  id: toolName,
  type: 'designer',
  featureGate: FEATURE_FLAGS.AIA_STYLE_CLEANUP_ARTIFACTS,
  permission: [{resource: 'SiteStyleBlock', action: 'modify'}],
  prettyName: 'Delete unused styles',
  prettyStatus: {
    executed: 'Deleted unused styles',
    executing: 'Deleting unused styles',
    failed: 'Failed to delete unused styles',
  },
  confirmBeforeExecuting: true, // Destructive: bulk-deletes style blocks
  timeout: 5000,
  llmTool: {
    name: toolName,
    description: dedent`
      Deletes the unused style classes returned by find-unused-styles. Use only after the user has confirmed deletion in chat and you have fresh styleBlockId values from a recent find-unused-styles call. Do not call when you only have class names or selectors — this tool requires styleBlockId values. All-or-none: if any id is no longer deletable, nothing is deleted. Deletion is undoable through the Designer's history.
    `,
    inputSchema: deleteUnusedStylesArgsSchema,
  },
});
